import { P as pointInSegment, B as signedArea2, T as pointInCircumCircle, f as eqDelta2, q as circumCenter2, Z as ZERO2, A as centroid, w as pointInPolygon2, H as sutherlandHodgeman, x as liangBarsky2 } from '../common/index-e6512bd8.js';
import { i as isNumber } from '../common/add-5cc35f4d.js';
import { E as EPS } from '../common/codegen-df7bb22c.js';
import '../common/distsq-d74f45eb.js';
import '../common/set-c051c4f9.js';

let NEXT_ID = 0;
/**
 * Quad-edge implementation after Guibas & Stolfi. Based on C++ versions
 * by Paul Heckbert, Dani Lischinski et al:
 *
 * References:
 *
 * - {@link http://www.cs.cmu.edu/afs/andrew/scs/cs/15-463/2001/pub/src/a2/quadedge.html}
 * - {@link http://www.cs.cmu.edu/afs/andrew/scs/cs/15-463/2001/pub/src/a2/lischinski/114.ps}
 */
class Edge {
    constructor(parent, id) {
        this.parent = parent;
        this.id = id;
    }
    /**
     * Main edge / quadedge factory function. Use this in preference of
     * direct invocation of the {@link Edge} constructor.
     *
     * Creates new {@link QuadEdge} with 4 child edges and returns the first
     * child/primary edge. If `src` and `dest` are not `null`ish, the
     * given args will be associated with that new edge as end points.
     *
     * @param src -
     * @param dest -
     */
    static create(src, dest) {
        const quad = new Array(4);
        const a = (quad[0] = new Edge(quad, NEXT_ID));
        const b = (quad[1] = new Edge(quad, NEXT_ID + 1));
        const c = (quad[2] = new Edge(quad, NEXT_ID + 2));
        const d = (quad[3] = new Edge(quad, NEXT_ID + 3));
        a.onext = a;
        c.onext = c;
        b.onext = d;
        d.onext = b;
        NEXT_ID += 4;
        src && dest && a.setEnds(src, dest);
        return a;
    }
    /**
     * Next CW edge from this edge's origin.
     */
    get oprev() {
        return this.rot.onext.rot;
    }
    /**
     * Dual of this edge, right -> left.
     */
    get rot() {
        return this.parent[(this.id + 1) & 3];
    }
    /**
     * Dual of this edge, left -> right.
     * I.e same as `this.rot.sym`
     */
    get invrot() {
        return this.parent[(this.id + 3) & 3];
    }
    /**
     * Symmetric partner edge of this edge, from dest -> src.
     * I.e. `this === this.sym.sym`
     */
    get sym() {
        return this.parent[(this.id + 2) & 3];
    }
    /**
     * Next CCW edge to this edge's dest.
     */
    get dnext() {
        return this.sym.onext.sym;
    }
    /**
     * Next CW edge to this edge's dest.
     */
    get dprev() {
        return this.invrot.onext.invrot;
    }
    /**
     * Next CCW edge around the left face (dual vertex) from this edge's
     * dest.
     */
    get lnext() {
        return this.invrot.onext.rot;
    }
    /**
     * Next CCW edge around the left face (dual vertex) to this edge's
     * origin.
     */
    get lprev() {
        return this.onext.sym;
    }
    /**
     * Next CCW edge around the right face (dual vertex) to this edge's
     * dest.
     */
    get rnext() {
        return this.rot.onext.invrot;
    }
    /**
     * Next CCW edge around the right face (dual vertex) to this edge's
     * origin.
     */
    get rprev() {
        return this.sym.onext;
    }
    /**
     * Returns this edge's dest vertex. I.e. `this.sym.origin`
     */
    get dest() {
        return this.sym.origin;
    }
    /**
     * Sets the origin & dest vertices of this edge (in other words, the
     * origins of this edge and `this.sym`).
     *
     * @param o -
     * @param d -
     */
    setEnds(o, d) {
        this.origin = o;
        this.sym.origin = d;
    }
    connect(e) {
        const n = Edge.create();
        n.splice(this.lnext);
        n.sym.splice(e);
        n.setEnds(this.dest, e.origin);
        return n;
    }
    swap() {
        const a = this.oprev;
        const b = this.sym.oprev;
        this.splice(a);
        this.sym.splice(b);
        this.splice(a.lnext);
        this.sym.splice(b.lnext);
        this.setEnds(a.dest, b.dest);
    }
    remove() {
        this.splice(this.oprev);
        this.sym.splice(this.sym.oprev);
        delete this.parent;
    }
    /**
     * Modifies the edge rings around the origins of this edge and `e`,
     * as well as, independently, the edge rings of both edges' left
     * dual vertex. In each case, if the rings are separate, this
     * operator will join them and if both rings are the same ring, they
     * will be split / separated. Therefore, splice` is it's own reverse
     * operator and the only operator needed to edit quad edge
     * topologies.
     *
     * @param e -
     */
    splice(e) {
        const alpha = this.onext.rot;
        const beta = e.onext.rot;
        const t1 = e.onext;
        const t2 = this.onext;
        const t3 = beta.onext;
        const t4 = alpha.onext;
        this.onext = t1;
        e.onext = t2;
        alpha.onext = t3;
        beta.onext = t4;
        return this;
    }
}

const rightOf = (p, e) => signedArea2(p, e.dest.pos, e.origin.pos) > 0;
class DVMesh {
    constructor(pts, size = 1e5) {
        const a = { pos: [0, -size], id: 0 };
        const b = { pos: [size, size], id: 1 };
        const c = { pos: [-size, size], id: 2 };
        const eab = Edge.create(a, b);
        const ebc = Edge.create(b, c);
        const eca = Edge.create(c, a);
        eab.sym.splice(ebc);
        ebc.sym.splice(eca);
        eca.sym.splice(eab);
        this.first = eab;
        this.nextID = 3;
        if (pts && pts.length) {
            isNumber(pts[0][0])
                ? this.addKeys(pts)
                : this.addAll(pts);
        }
        else {
            this.computeDual();
        }
    }
    /**
     * Adds a single new point `p` w/ optional value `val` to the mesh, unless
     * there already is another point existing within radius `eps`. If `update`
     * is true (default), the mesh dual will be automatically updated using
     * {@link DVMesh.computeDual}.
     *
     * @remarks
     * If adding multiple points, ensure `computeDual` will only be called
     * for/after the last point insertion to avoid computational overhead.
     *
     * @param p
     * @param val
     * @param eps
     * @param update
     */
    add(p, val, eps = EPS, update = true) {
        let [e, exists] = this.locate(p, eps);
        if (exists)
            return false;
        if (pointInSegment(p, e.origin.pos, e.dest.pos)) {
            e = e.oprev;
            e.onext.remove();
        }
        let base = Edge.create(e.origin, {
            pos: p,
            id: this.nextID++,
            val,
        });
        base.splice(e);
        const first = base;
        do {
            base = e.connect(base.sym);
            e = base.oprev;
        } while (e.lnext !== first);
        // enforce delaunay constraints
        do {
            const t = e.oprev;
            if (rightOf(t.dest.pos, e) &&
                pointInCircumCircle(e.origin.pos, t.dest.pos, e.dest.pos, p)) {
                e.swap();
                e = e.oprev;
            }
            else if (e.onext !== first) {
                e = e.onext.lprev;
            }
            else {
                break;
            }
        } while (true);
        update && this.computeDual();
        return true;
    }
    addKeys(pts, eps) {
        for (let p of pts) {
            this.add(p, undefined, eps, false);
        }
        this.computeDual();
    }
    addAll(pairs, eps) {
        for (let p of pairs) {
            this.add(p[0], p[1], eps, false);
        }
        this.computeDual();
    }
    /**
     * Returns tuple of the edge related to `p` and a boolean to indicate if
     * `p` already exists in this triangulation (true if already present).
     *
     * @param p - query point
     */
    locate(p, eps = EPS) {
        let e = this.first;
        while (true) {
            if (eqDelta2(p, e.origin.pos, eps) ||
                eqDelta2(p, e.dest.pos, eps)) {
                return [e, true];
            }
            else if (rightOf(p, e)) {
                e = e.sym;
            }
            else if (!rightOf(p, e.onext)) {
                e = e.onext;
            }
            else if (!rightOf(p, e.dprev)) {
                e = e.dprev;
            }
            else {
                return [e, false];
            }
        }
    }
    /**
     * Syncronize / update / add dual faces (i.e. Voronoi) for current
     * primary mesh (i.e. Delaunay).
     */
    computeDual() {
        const work = [this.first.rot];
        const visitedEdges = {};
        const visitedVerts = {};
        while (work.length) {
            const e = work.pop();
            if (visitedEdges[e.id])
                continue;
            visitedEdges[e.id] = true;
            if (!e.origin || !visitedVerts[e.origin.id]) {
                let t = e.rot;
                const a = t.origin.pos;
                let isBoundary = t.origin.id < 3;
                t = t.lnext;
                const b = t.origin.pos;
                isBoundary = isBoundary && t.origin.id < 3;
                t = t.lnext;
                const c = t.origin.pos;
                isBoundary = isBoundary && t.origin.id < 3;
                const id = this.nextID++;
                e.origin = {
                    pos: !isBoundary ? circumCenter2(a, b, c) : ZERO2,
                    id,
                };
                visitedVerts[id] = true;
            }
            work.push(e.sym, e.onext, e.lnext);
        }
    }
    delaunay(bounds) {
        const cells = [];
        const usedEdges = {};
        const bc = bounds && centroid(bounds);
        this.traverse((eab) => {
            if (!usedEdges[eab.id]) {
                const ebc = eab.lnext;
                const eca = ebc.lnext;
                const va = eab.origin.pos;
                const vb = ebc.origin.pos;
                const vc = eca.origin.pos;
                let verts = [va, vb, vc];
                if (bounds &&
                    !(pointInPolygon2(va, bounds) &&
                        pointInPolygon2(vb, bounds) &&
                        pointInPolygon2(vc, bounds))) {
                    verts = sutherlandHodgeman(verts, bounds, bc);
                    if (verts.length > 2) {
                        cells.push(verts);
                    }
                }
                else {
                    cells.push(verts);
                }
                usedEdges[eab.id] = usedEdges[ebc.id] = usedEdges[eca.id] = true;
            }
        });
        return cells;
    }
    voronoi(bounds) {
        const cells = [];
        const bc = bounds && centroid(bounds);
        this.traverse(bounds
            ? (e) => {
                const first = (e = e.rot);
                let verts = [];
                let needsClip = false;
                let p;
                do {
                    p = e.origin.pos;
                    verts.push(p);
                    needsClip = needsClip || !pointInPolygon2(p, bounds);
                } while ((e = e.lnext) !== first);
                if (needsClip) {
                    verts = sutherlandHodgeman(verts, bounds, bc);
                    if (verts.length < 3)
                        return;
                }
                cells.push(verts);
            }
            : (e) => {
                const first = (e = e.rot);
                const verts = [];
                do {
                    verts.push(e.origin.pos);
                } while ((e = e.lnext) !== first);
                cells.push(verts);
            }, false);
        return cells;
    }
    edges(voronoi = false, boundsMinMax) {
        const edges = [];
        const visitedEdges = {};
        this.traverse((e) => {
            if (visitedEdges[e.id] || visitedEdges[e.sym.id])
                return;
            if (e.origin.id > 2 && e.dest.id > 2) {
                const a = e.origin.pos;
                const b = e.dest.pos;
                if (boundsMinMax) {
                    const clip = liangBarsky2(a, b, boundsMinMax[0], boundsMinMax[1]);
                    clip && edges.push([clip[0], clip[1]]);
                }
                else {
                    edges.push([a, b]);
                }
            }
            visitedEdges[e.id] = true;
        }, true, voronoi ? this.first.rot : this.first);
        return edges;
    }
    traverse(proc, edges = true, e = this.first) {
        const work = [e];
        const visitedEdges = {};
        const visitedVerts = {};
        while (work.length) {
            e = work.pop();
            if (visitedEdges[e.id])
                continue;
            visitedEdges[e.id] = true;
            const eoID = e.origin.id;
            if (eoID > 2 && e.rot.origin.id > 2) {
                if (edges || !visitedVerts[eoID]) {
                    visitedVerts[eoID] = true;
                    proc(e, visitedEdges, visitedVerts);
                }
            }
            work.push(e.sym, e.onext, e.lnext);
        }
    }
}

export { DVMesh };
